import type { SessionId, ProductId, RequestId, CorrelationId, ConfigurationId, WorkingConfigurationId, ProductVersionId, VisitorId } from './ids';

export interface ContractContext {
  contractVersion: string;
  tenantId?: string;
  accountId?: string;
  userId?: string;
  visitorId?: VisitorId;
  sessionId: SessionId;
  viewerSessionId?: string;
  configurationId?: ConfigurationId;
  workingConfigurationId?: WorkingConfigurationId;
  productId: ProductId;
  productVersionId?: ProductVersionId;
  modelId?: string;
  modelVersionId?: string;
  requestId: RequestId;
  correlationId: CorrelationId;
  causationId?: string;
  revision?: number;
  timestamp: string;
  locale?: string;
  currency?: string;
}

export const CONTRACT_VERSION = '1.0.0';

let counter = 0;

export function generateId(prefix: string): string {
  counter += 1;
  const ts = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `${prefix}_${ts}_${rand}_${counter}`;
}

export function generateSessionId(): SessionId {
  return generateId('sess');
}

export function generateRequestId(): RequestId {
  return generateId('req');
}

export function generateCorrelationId(): CorrelationId {
  return generateId('cor');
}

export interface CreateContextOptions {
  sessionId: SessionId;
  productId: ProductId;
  revision?: number;
  configurationId?: ConfigurationId;
  workingConfigurationId?: WorkingConfigurationId;
  correlationId?: CorrelationId;
  causationId?: string;
  visitorId?: VisitorId;
}

export function createContractContext(opts: CreateContextOptions): ContractContext {
  return {
    contractVersion: CONTRACT_VERSION,
    sessionId: opts.sessionId,
    productId: opts.productId,
    revision: opts.revision,
    configurationId: opts.configurationId,
    workingConfigurationId: opts.workingConfigurationId,
    requestId: generateRequestId(),
    correlationId: opts.correlationId ?? generateCorrelationId(),
    causationId: opts.causationId,
    visitorId: opts.visitorId,
    timestamp: new Date().toISOString(),
  };
}

export function deriveContext(
  parent: ContractContext,
  overrides?: Partial<CreateContextOptions>,
): ContractContext {
  return createContractContext({
    sessionId: overrides?.sessionId ?? parent.sessionId,
    productId: overrides?.productId ?? parent.productId,
    revision: overrides?.revision ?? parent.revision,
    configurationId: overrides?.configurationId ?? parent.configurationId,
    workingConfigurationId: overrides?.workingConfigurationId ?? parent.workingConfigurationId,
    correlationId: parent.correlationId,
    causationId: parent.requestId,
    visitorId: overrides?.visitorId ?? parent.visitorId,
  });
}
